/**
 * Intake — the programme intake a portfolio invited an entity into.
 *
 * Figures S6 (the "Intake 2" filter pill and the "By intake" ordering) and C1
 * ("Intake · set by you on the invite, editable here").
 *
 * WHERE IT LIVES, AND WHY. Nothing on the platform records an intake: the
 * entity row has no such column and no route writes one. It is also not the
 * inventor's fact — it is the portfolio's own label for when it brought this
 * inventor in, which is exactly the shape of an aggregator preference. So it
 * is stored per aggregator through the same server-side preferences route
 * (GET/PUT /users/preferences) that already holds this portfolio's group-by,
 * filters and entity tags, under its own key. It is real, persisted, and the
 * portfolio's alone; it is not a column on the inventor's record.
 *
 * The pure helpers are exported separately from the hook so they can be
 * tested without a provider.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getUserPreference, setUserPreference } from '../../services/api'

/** entity_id -> the portfolio's intake label for it, e.g. "Intake 2". */
export type IntakeTags = Record<string, string>

export function intakeKey(aggregatorId: string): string {
  return `aggregator_intake:${aggregatorId}`
}

/** The intake label for one entity, or null when the portfolio has set none. */
export function intakeFor(tags: IntakeTags, entityId: string | undefined): string | null {
  if (!entityId) return null
  const value = tags[entityId]
  return value && value.trim() ? value.trim() : null
}

/**
 * The intake label for a whole group of owners. A group carries an intake
 * only when its owners agree on one — two owners brought in under different
 * intakes have no single answer, and inventing one would be a false label.
 */
export function intakeForGroup(tags: IntakeTags, entityIds: string[]): string | null {
  const labels = new Set(
    entityIds.map((id) => intakeFor(tags, id)).filter((l): l is string => !!l),
  )
  return labels.size === 1 ? [...labels][0] : null
}

/** Every intake this portfolio has used, in a stable order — the filter's menu. */
export function knownIntakes(tags: IntakeTags): string[] {
  return [...new Set(Object.values(tags).map((v) => v.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'en-AU', { numeric: true }),
  )
}

/** Merge one entity's intake into the blob, dropping it when cleared. */
export function withIntake(tags: IntakeTags, entityId: string, value: string): IntakeTags {
  const next = { ...tags }
  if (value.trim()) next[entityId] = value.trim()
  else delete next[entityId]
  return next
}

function readCache(key: string): IntakeTags {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as IntakeTags) : {}
  } catch {
    return {}
  }
}

function writeCache(key: string, value: IntakeTags): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* a cache that cannot be written is not an error — the server holds it */
  }
}

/**
 * One entity_id -> label map, held per aggregator in the server-side
 * preferences route. Same write-through shape as useAggregatorPreferences: the
 * server is the source of truth, localStorage is an offline fallback, and a
 * write is always a read-modify-write of the whole blob so two edits in one
 * session cannot drop each other.
 *
 * Generic because the intake label and the entity-group label are the same
 * kind of thing — a name THIS PORTFOLIO holds for an inventor, which no table
 * on the platform carries — and having one implementation means they cannot
 * drift apart in how they save, cache or fail.
 */
export function useAggregatorTagMap(
  aggregatorId: string | null | undefined,
  prefKey: (aggId: string) => string,
  queryKeyName: string,
) {
  const queryClient = useQueryClient()
  const aggId = aggregatorId ?? ''
  const key = prefKey(aggId)
  const queryKey = [queryKeyName, aggId]

  const { data, error } = useQuery({
    queryKey,
    queryFn: () => getUserPreference(key),
    enabled: !!aggId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const tags: IntakeTags = data && !error ? ((data.value as IntakeTags) ?? {}) : readCache(key)

  const setTag = async (entityId: string, value: string) => {
    const cached = queryClient.getQueryData<{ value: IntakeTags }>(queryKey)
    const current = cached?.value ?? readCache(key)
    const merged = withIntake(current, entityId, value)
    queryClient.setQueryData(queryKey, { pref_key: key, value: merged })
    writeCache(key, merged)
    if (aggId) await setUserPreference(key, merged).catch(() => undefined)
  }

  return { tags, setTag }
}

/** Read and write this portfolio's intake labels. */
export function useIntakeTags(aggregatorId: string | null | undefined) {
  const { tags, setTag } = useAggregatorTagMap(aggregatorId, intakeKey, 'aggregator_intake')
  return { intakeTags: tags, setIntake: setTag }
}
